/**
 * Visual effects + enhancements (3rd-party driven).
 *
 * Put here:
 * - ScrollReveal / Lozad / Fancybox initialization glue
 * - Feature-detection before calling globals
 *
 * Avoid here:
 * - Creating/owning the libraries themselves (loaded elsewhere)
 */
function applyCssDeclaration(node, declaration) {
	String(declaration)
		.split(";")
		.forEach((pair) => {
			const idx = pair.indexOf(":");
			if (idx === -1) return;
			const property = pair.slice(0, idx).trim();
			const value = pair.slice(idx + 1).trim();
			if (property && value) node.style[property] = value;
		});
}

function delayFromNode(node, fallback) {
	const raw = node.getAttribute("data-delay");
	if (raw == null || raw === "") return fallback;
	const delay = Number(raw);
	return Number.isFinite(delay) ? delay : fallback;
}

function revealGroup(sr, selector, options) {
	document.querySelectorAll(selector).forEach((node) => {
		sr.reveal(node, {
			...options,
			delay: delayFromNode(node, options.delay),
		});
	});
}

function playObservedReveals(sr) {
	const nodes = document.querySelectorAll("[data-sr-id]");
	if (!nodes.length || typeof sr.delegate !== "function") return;

	const queued = new Set();
	let flushId = 0;

	const flush = () => {
		flushId = 0;
		queued.forEach((el) => {
			if (el.config && el.config.viewOffset) {
				el.config.viewOffset.bottom = 0;
			}
			const node = el.node;
			if (!node || !el.styles) return;
			const hidden = [
				el.styles.inline && el.styles.inline.generated,
				el.styles.opacity && el.styles.opacity.generated,
				el.styles.transform && el.styles.transform.generated.initial,
				"transition: none",
			]
				.filter(Boolean)
				.join("; ");
			applyCssDeclaration(node, hidden);
			el.revealed = false;
			el.seen = false;
		});
		void document.body.offsetHeight;
		queued.clear();
		requestAnimationFrame(() => {
			sr.delegate();
		});
	};

	const queue = (node) => {
		const id = node.getAttribute("data-sr-id");
		const el = sr.store && sr.store.elements && sr.store.elements[id];
		if (!el) return;
		queued.add(el);
		if (!flushId) flushId = requestAnimationFrame(flush);
	};

	if (typeof IntersectionObserver === "undefined") {
		nodes.forEach(queue);
		return;
	}

	const io = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				io.unobserve(entry.target);
				queue(entry.target);
			});
		},
		{ threshold: 0, rootMargin: "0px" }
	);

	nodes.forEach((node) => io.observe(node));
}

export function initEffects() {
	const isSinglePage = document.body.classList.contains("single");

	if (
		!isSinglePage &&
		typeof ScrollReveal !== "undefined" &&
		window.innerWidth > 1024
	) {
		const sr = ScrollReveal({
			duration: 1000,
			distance: "20px",
			easing: "cubic-bezier(0.5, 0, 0, 1)",
			reset: false,
			mobile: false,
			useDelay: "always",
			// Keep SR from auto-revealing. IntersectionObserver plays the
			// transition even when the node is already on screen (short
			// laptops, home-smile under the banner, refresh, etc.).
			viewOffset: {
				top: 0,
				right: 0,
				bottom: 1e5,
				left: 0,
			},
		});

		if (!sr.noop) {
			revealGroup(sr, ".fd-up", {
				origin: "bottom",
				delay: 200,
			});

			revealGroup(sr, ".fd-down", {
				origin: "top",
				delay: 200,
			});

			revealGroup(sr, ".fd-left", {
				origin: "right",
				delay: 200,
			});

			revealGroup(sr, ".fd-right", {
				origin: "left",
				delay: 200,
			});

			revealGroup(sr, ".fd-in", {
				scale: 0.9,
				opacity: 0,
				delay: 200,
				distance: "0px",
			});

			sr.reveal(".stagger > *", {
				origin: "bottom",
				interval: 120,
			});

			window.setTimeout(() => {
				requestAnimationFrame(() => {
					requestAnimationFrame(() => playObservedReveals(sr));
				});
			}, 0);
		}
	}


	// Fancybox
	if (typeof Fancybox !== "undefined") {
		// Automatically disable Fancybox drag on responsive tables so native horizontal scrolling works
		document.querySelectorAll(".table-responsive, .table-wrap, .table-res").forEach((el) => {
			el.setAttribute("data-fancybox-no-drag", "");
		});

		Fancybox.bind("[data-fancybox]");
		document.addEventListener("click", (e) => {
			if (e.target.closest(".close-modal")) {
				e.preventDefault();
				Fancybox.close();
			}
		});
	}
}